import { FC, useMemo, useState } from "react";
import { IEnvironment } from "../../interfaces";
import { EnvironmentsList } from "./EnvironmentsList";
import { useEnvironments, useInternalEnvironments } from "./Environments.hook";
import "./style.css";

const matchesQuery = (env: IEnvironment, query: string) => {
  const normalizedQuery = query.trim().toLowerCase();
  if (!normalizedQuery) {
    return true;
  }

  return [
    env?.name,
    env?.isoCode,
    env?.rm,
    env?.cluster?.trim() || "Single-tenant",
    env?.uat,
    env?.prod,
    env?.url,
    ...(env?.additionalDomains ?? []),
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase()
    .includes(normalizedQuery);
};

export const Environments: FC = () => {
  const [query, setQuery] = useState("");
  const { state, headers } = useEnvironments();

  const { state: internalState, headers: internalHeaders } =
    useInternalEnvironments();

  const envs = state.envs ?? [];
  const internalEnvs = internalState.envs ?? [];

  const filteredInternal = useMemo(
    () => internalEnvs.filter((env) => matchesQuery(env, query)),
    [internalEnvs, query]
  );
  const filteredCountries = useMemo(
    () => envs.filter((env) => matchesQuery(env, query)),
    [envs, query]
  );

  const total = internalEnvs.length + envs.length;
  const visible = filteredInternal.length + filteredCountries.length;
  const isFiltering = query.trim().length > 0;

  return (
    <div className="environments">
      <label className="environments-filter">
        <span>
          Filter ({visible} of {total})
        </span>
        <input
          value={query}
          placeholder="Country, code, RM, cluster or url"
          onChange={(event) => setQuery(event.target.value)}
        />
      </label>
      {isFiltering && visible === 0 && (
        <p>No environments match this filter.</p>
      )}
      {(!isFiltering || filteredInternal.length > 0) && (
        <>
          <h2 className="table-header">Internal ({filteredInternal.length})</h2>
          <table className="environment-list">
            <EnvironmentsList
              envs={filteredInternal}
              headers={internalHeaders}
            />
          </table>
        </>
      )}
      {(!isFiltering || filteredCountries.length > 0) && (
        <>
          <h2 className="table-header">
            Countries ({filteredCountries.length})
          </h2>
          <table className="environment-list">
            <EnvironmentsList envs={filteredCountries} headers={headers} />
          </table>
        </>
      )}
    </div>
  );
};
